/**
 * The pay sheet: the one-tap confirm moment.
 *
 * Binding doctrine, from CLAUDE.md (Alan, 2026-07-21): the card is vaulted
 * ONCE, and every purchase after that is a tap on this sheet with an immediate
 * answer. No checkout redirect, no form, no re-entering a card. A flow that
 * cannot do that is not shippable.
 *
 * Generic on purpose: a briefing and a top-up are the same moment, so they
 * share one component and therefore one set of states. `settle` is the async
 * function the tap runs, and it returns the raw { status, body } so each caller
 * maps its own outcomes - which is also why it must not throw on 401. See
 * `raw()` in api/client.ts.
 *
 * @module web/components/PaySheet
 */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { RawResponse } from "../api/client.ts";
import type { BriefSubmissionResponse } from "../api/types.ts";
import { fmtEurCompact } from "../money.ts";
import {
  confirmationFailureMessage,
  isConfirmationInProgress,
  isConfirmationTerminal,
  submissionStatus,
  type ConfirmationObservationBody,
} from "../screens/app/confirmation.ts";

export interface PaySheetSpec {
  eyebrow: string;
  title: string;
  amountCents: number;
  icon: ReactNode;
  label: string;
  cta: string;
  note: string;
  blocked?: boolean;
  processingNote?: string;
  /** The tap. Returns the status the sheet maps to a sentence. */
  settle: () => Promise<RawResponse<{ charged?: boolean; declined?: boolean; error?: string; code?: string; processing?: boolean; submission?: { status?: string; message?: string | null; amountDueCents?: number } }>>;
  onQuoteChanged?: (submission: BriefSubmissionResponse) => void;
  /** Poll the durable operation after a 202/processing response. */
  observe?: () => Promise<RawResponse<ConfirmationObservationBody>>;
  /** Success wording, from the response body. */
  done?: (body: { charged?: boolean } | null) => { h: string; s: string };
  /** Runs once the checkmark has been seen. */
  after?: () => void;
}

/**
 * What the sheet says for each status.
 *
 * The sheet speaks the UI's language; the server's message is only the fallback
 * for a state we have no words of our own for. Every one of these keeps the
 * sheet OPEN. Pending work is observed automatically; a terminal failure returns
 * an explicit Try again action without claiming the order completed.
 */
const MESSAGES: Record<number, string> = {
  202: "Payment is still being confirmed - tap again in a moment.",
  409: "This briefing was already handled.",
  502: "Something went wrong on our side.",
  0: "No connection. Try again when you are ready.",
};

type PayResponseBody = {
  charged?: boolean;
  declined?: boolean;
  error?: string;
  processing?: boolean;
  code?: string;
  submission?: { status?: string; message?: string | null; amountDueCents?: number };
};

export function PaySheet({ spec, onClose }: { spec: PaySheetSpec | null; onClose: () => void }) {
  const [state, setState] = useState<"ask" | "processing" | "done">("ask");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneText, setDoneText] = useState({ h: "Confirmed", s: "" });
  const [displayAmountCents, setDisplayAmountCents] = useState(spec?.amountCents ?? 0);
  const operation = useRef(0);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Closing slides the sheet away before it unmounts: keep the last spec on
  // screen for the length of the .pay-sheet close transition (260ms).
  // Derived during render, not in an effect: an effect lands one render late,
  // and that render would already have unmounted the sheet.
  const [shown, setShown] = useState(spec);
  if (spec && spec !== shown) setShown(spec);
  useEffect(() => {
    if (spec || !shown) return;
    const t = setTimeout(() => setShown(null), 280);
    return () => clearTimeout(t);
  }, [spec, shown]);

  // A newly opened sheet is always a fresh ask, never the previous outcome.
  // Invalidate the old operation too: Recent can open a different quoted brief
  // in the same component, and an older response must never settle the new one.
  useEffect(() => {
    operation.current += 1;
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    closeTimer.current = null;
    if (spec) {
      setState("ask");
      setBusy(false);
      setError(null);
      setDisplayAmountCents(spec.amountCents);
    }
    return () => {
      operation.current += 1;
      if (closeTimer.current !== null) clearTimeout(closeTimer.current);
      closeTimer.current = null;
    };
  }, [spec]);

  // Durable confirmation continues on the server, so the client may close the
  // sheet while it is processing. Only the short request-bound paying state is
  // non-dismissible.
  const dismissible = state === "processing" || (!busy && state === "ask");
  useEffect(() => {
    if (!spec) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && dismissible) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [spec, dismissible, onClose]);

  const view = spec ?? shown;
  if (!view) return null;

  function finishSuccess(run: number, activeSpec: PaySheetSpec, body: { charged?: boolean } | null): void {
    if (run !== operation.current) return;
    const text = activeSpec.done?.(body) ?? { h: "Confirmed", s: "" };
    setDoneText(text);
    setState("done");
    setBusy(false);
    const after = activeSpec.after;
    closeTimer.current = setTimeout(() => {
      if (run !== operation.current) return;
      onClose();
      // Let the sheet slide away first: `after` usually navigates, which
      // unmounts the screen - and this sheet with it - mid-slide.
      if (after) setTimeout(after, 280);
    }, 1600);
  }

  async function observeCompletion(run: number, activeSpec: PaySheetSpec): Promise<void> {
    if (!activeSpec.observe) {
      setState("ask");
      setBusy(false);
      setError("Your confirmation is still being processed. Reopen this briefing to check it.");
      return;
    }

    // A pending Whop payment is normal for minutes, not a client-side failure.
    // Keep observing for as long as this sheet owns the operation; the server's
    // durable worker remains the source of truth until it confirms or fails.
    let delay = 800;
    while (run === operation.current) {
      await new Promise<void>(resolve => setTimeout(resolve, delay));
      if (run !== operation.current) return;

      let observed: RawResponse<ConfirmationObservationBody>;
      try {
        observed = await activeSpec.observe();
      } catch {
        observed = { status: 0, body: null };
      }
      if (run !== operation.current) return;

      const status = submissionStatus(observed.body);
      if (status === "confirmed") {
        finishSuccess(run, activeSpec, observed.body ? {} : null);
        return;
      }
      if (isConfirmationTerminal(status)) {
        const amountDue = observed.body?.submission?.amountDueCents;
        if (typeof amountDue === "number") setDisplayAmountCents(amountDue);
        setState("ask");
        setBusy(false);
        setError(confirmationFailureMessage(observed.body));
        return;
      }
      delay = Math.min(4_000, Math.round(delay * 1.5));
    }
  }

  async function pay() {
    if (!spec || state === "processing") return;
    const run = ++operation.current;
    const activeSpec = spec;
    setBusy(true);
    setError(null);
    let res: RawResponse<PayResponseBody>;
    try {
      res = await activeSpec.settle();
    } catch {
      res = { status: 0, body: null };
    }

    if (run !== operation.current) return;

    if (res.status === 200) {
      finishSuccess(run, activeSpec, res.body);
      return;
    }
    if (res.status === 409 && res.body?.code === "quote_changed" && res.body.submission && activeSpec.onQuoteChanged) {
      activeSpec.onQuoteChanged(res.body.submission as BriefSubmissionResponse);
      setBusy(false);
      return;
    }

    const serverStatus = submissionStatus(res.body);
    if (activeSpec.observe && (
      res.status === 202 ||
      res.body?.processing === true ||
      isConfirmationInProgress(serverStatus)
    )) {
      setState("processing");
      setBusy(true);
      setError(null);
      void observeCompletion(run, activeSpec);
      return;
    }

    const amountDue = res.body?.submission?.amountDueCents;
    if (typeof amountDue === "number") setDisplayAmountCents(amountDue);

    // 402 needs the body to tell a decline from an empty balance.
    const message = res.status === 402
      ? (res.body?.declined
        ? "Payment failed - choose Try again for a new attempt."
        : "Your balance doesn't cover this - top up first.")
      : res.body?.error ?? MESSAGES[res.status] ?? "Could not confirm. Try again when you are ready.";

    setError(message);
    setBusy(false);
  }

  const retryLabel = "Try again";
  return (
    <>
      <div className={`pay-sheet-overlay${spec ? " open" : ""}`} onClick={() => { if (spec && dismissible) onClose(); }} />
      <div className={`pay-sheet${spec ? " open" : ""}`} data-state={state} role="dialog" aria-modal="true" aria-labelledby="ps-title">
        <div className="pay-grip" />
        <div className="pay-body" aria-hidden={state === "done"}>
          <div className="pay-eyebrow">{view.eyebrow}</div>
          <h2 className="pay-title" id="ps-title">{view.title}</h2>
          <div className="pay-row">
            <span className="pay-row-k">Total</span>
            <span className="pay-amount" id="ps-amount">{fmtEurCompact(displayAmountCents)}</span>
          </div>
          <div className="pay-row">
            <span className="pay-row-k">Pay with</span>
            <span className="pay-row-v pay-source">{view.icon}<span>{view.label}</span></span>
          </div>
          <button className="pay-cta" disabled={view.blocked || busy || !spec} aria-busy={busy} onClick={() => void pay()}>
            {state === "processing" ? "Confirming…" : busy ? "Paying…" : error ? retryLabel : view.cta}
          </button>
          <button className="pay-cancel" disabled={!dismissible || !spec} onClick={onClose}>
            {state === "processing" ? "Close" : "Cancel"}
          </button>
          <p className={`pay-note${error || view.blocked ? " err" : ""}`}>
            {state === "processing" ? (view.processingNote ?? "Your payment and briefing are being confirmed automatically. You can close this window.") : error ?? view.note}
          </p>
        </div>
        <div className="pay-done">
          <div className="pay-check">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="pay-done-h">{doneText.h}</div>
          <div className="pay-done-s">{doneText.s}</div>
        </div>
      </div>
    </>
  );
}
